import React, { useState, useEffect, useRef, useCallback } from 'react';
import { api } from '../../shared/utils/api';
import QuestionCard from './QuestionCard';
import QuestionResult from './QuestionResult';
import ExamReview from './ExamReview';
import SessionSummary from './SessionSummary';

function fmtTimer(s) {
  const m = Math.floor(s / 60);
  const rem = s % 60;
  return `${m}:${String(rem).padStart(2, '0')}`;
}

const EXAM_SECS_PER_Q = 150;

export default function QuestionSession({ config, onExit }) {
  const sessionType = config.sessionType || 'practice';
  const isExam = sessionType === 'exam';
  const totalExamSecs = (config.count || 10) * EXAM_SECS_PER_Q;

  const [questions, setQuestions] = useState([]);
  const [sessionId, setSessionId] = useState(null);
  const [answers, setAnswers] = useState([]); // practice: array pushed in order; exam: sparse by index
  const [selections, setSelections] = useState({}); // exam: { [idx]: 'A' } — current selection per question
  const [currentIdx, setCurrentIdx] = useState(0);
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [examPhase, setExamPhase] = useState('answering'); // answering | review | graded
  const [examTotalSecs, setExamTotalSecs] = useState(null);
  const [elapsedSecs, setElapsedSecs] = useState(0);
  const [countdown, setCountdown] = useState(totalExamSecs);
  const [flagged, setFlagged] = useState({});
  const [submittingExam, setSubmittingExam] = useState(false);

  const questionStartTime = useRef(null);
  const timerRef = useRef(null);

  // Load questions
  useEffect(() => {
    const body = {
      mode: config.mode,
      session_type: sessionType,
      count: config.count || 10,
    };
    if (config.module && config.module !== 'both') body.module = config.module;
    if (config.topicIds && config.topicIds.length > 0) body.topic_ids = config.topicIds;

    api('/api/questions/practice', {
      method: 'POST',
      body: JSON.stringify(body),
    })
      .then((data) => {
        if (!data.questions || data.questions.length === 0) {
          setError(
            "We don't have practice questions for this topic yet. Try selecting a different topic or using the \"Weakest topics\" mode."
          );
          setLoading(false);
          return;
        }
        setQuestions(data.questions);
        setSessionId(data.session_id);
        setLoading(false);
      })
      .catch(() => {
        setError('Failed to load questions. Please try again.');
        setLoading(false);
      });
    // eslint-disable-next-line
  }, []);

  // Timer effect
  useEffect(() => {
    if (loading || examPhase === 'graded') return;
    if (!isExam && submitted) return; // freeze timer on practice result view
    questionStartTime.current = Date.now();
    if (!isExam) setElapsedSecs(0);

    timerRef.current = setInterval(() => {
      if (isExam) {
        setCountdown((prev) => {
          if (prev <= 1) {
            clearInterval(timerRef.current);
            setExamPhase('review');
            return 0;
          }
          return prev - 1;
        });
      } else {
        setElapsedSecs(Math.floor((Date.now() - questionStartTime.current) / 1000));
      }
    }, 1000);
    return () => clearInterval(timerRef.current);
  }, [currentIdx, loading, submitted, isExam, examPhase]);

  // ───────── Practice: submit on option click ─────────
  const handlePracticeSubmit = useCallback(
    async (selectedAnswer) => {
      clearInterval(timerRef.current);
      const responseTime = questionStartTime.current
        ? (Date.now() - questionStartTime.current) / 1000
        : null;
      const question = questions[currentIdx];

      setSubmitted(true);
      let newAnswer;
      try {
        const result = await api('/api/questions/practice/answer', {
          method: 'POST',
          body: JSON.stringify({
            question_id: question.id,
            selected_answer: selectedAnswer,
            response_time_seconds: responseTime ? Math.round(responseTime) : null,
            session_id: sessionId,
          }),
        });
        newAnswer = { ...result, response_time_seconds: responseTime };
      } catch {
        newAnswer = {
          correct: false,
          correct_answer: '?',
          explanation: 'Could not record answer.',
          distractor_misconceptions: null,
          selected_answer: selectedAnswer,
          response_time_seconds: responseTime,
        };
      }
      setAnswers((prev) => {
        const next = [...prev];
        next[currentIdx] = newAnswer;
        return next;
      });
    },
    [currentIdx, questions, sessionId]
  );

  const handleNextPractice = useCallback(() => {
    const total = questions.length;
    if (currentIdx + 1 >= total) {
      // Last question → summary
      setExamPhase('graded');
    } else {
      setCurrentIdx((i) => i + 1);
      setSubmitted(false);
    }
  }, [currentIdx, questions.length]);

  // ───────── Exam: selection only, no immediate submit ─────────
  const handleExamSelect = useCallback(
    async (selectedAnswer) => {
      const question = questions[currentIdx];
      // Optimistic: store selection
      setSelections((prev) => ({ ...prev, [currentIdx]: selectedAnswer }));

      const responseTime = questionStartTime.current
        ? (Date.now() - questionStartTime.current) / 1000
        : null;

      try {
        await api('/api/questions/practice/answer', {
          method: 'POST',
          body: JSON.stringify({
            question_id: question.id,
            selected_answer: selectedAnswer,
            response_time_seconds: responseTime ? Math.round(responseTime) : null,
            session_id: sessionId,
          }),
        });
        // In exam mode the backend returns { saved: true, selected_answer }
        // without correct/explanation. Keep answers sparse but mark as answered.
        setAnswers((prev) => {
          const next = [...prev];
          next[currentIdx] = {
            selected_answer: selectedAnswer,
            response_time_seconds: responseTime,
          };
          return next;
        });
      } catch {
        // leave selection in place even on network error
      }
    },
    [currentIdx, questions, sessionId]
  );

  const handleExamPrev = useCallback(() => {
    setCurrentIdx((i) => Math.max(0, i - 1));
  }, []);

  const handleExamNext = useCallback(() => {
    if (currentIdx + 1 >= questions.length) {
      setExamPhase('review');
    } else {
      setCurrentIdx((i) => i + 1);
    }
  }, [currentIdx, questions.length]);

  // Flag toggle (exam mode)
  const toggleFlag = useCallback(async () => {
    const wasFlagged = !!flagged[currentIdx];
    const nextFlagged = !wasFlagged;
    setFlagged((prev) => ({ ...prev, [currentIdx]: nextFlagged }));
    const question = questions[currentIdx];
    if (!question || !sessionId) return;
    try {
      await api('/api/questions/flag', {
        method: 'POST',
        body: JSON.stringify({
          session_id: sessionId,
          question_id: question.id,
          flagged: nextFlagged,
        }),
      });
    } catch {
      // revert on failure
      setFlagged((prev) => ({ ...prev, [currentIdx]: wasFlagged }));
    }
  }, [currentIdx, flagged, questions, sessionId]);

  // Submit exam → fetch graded results
  const handleSubmitExam = useCallback(async () => {
    if (submittingExam) return;
    setSubmittingExam(true);
    try {
      const res = await api('/api/questions/submit-exam', {
        method: 'POST',
        body: JSON.stringify({ session_id: sessionId }),
      });
      const resultsByQid = {};
      (res.results || []).forEach((r) => {
        resultsByQid[r.question_id] = r;
      });
      const graded = questions.map((q, i) => {
        const r = resultsByQid[q.id];
        if (!r) return answers[i] || null;
        return {
          selected_answer: r.selected_answer,
          correct: !!r.correct,
          correct_answer: r.correct_answer,
          explanation: r.explanation,
          distractor_misconceptions: r.distractor_misconceptions,
          response_time_seconds: r.response_time_seconds,
        };
      });
      setAnswers(graded);
      setExamTotalSecs((res.session && res.session.total_time_seconds) || null);
      setExamPhase('graded');
    } catch {
      setError('Could not submit exam. Please try again.');
    } finally {
      setSubmittingExam(false);
    }
  }, [sessionId, questions, answers, submittingExam]);

  if (loading) {
    return <div className="text-sm text-text-muted py-sp-10 text-center">Loading questions…</div>;
  }

  if (error) {
    return (
      <div className="bg-bg-card rounded-r-xl border border-border-soft">
        <div className="px-sp-6 py-sp-10">
          <div className="text-center">
            <svg
              className="w-12 h-12 mx-auto mb-sp-3 text-text-muted"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
            >
              <circle cx="12" cy="12" r="10"></circle>
              <path d="M9 9h.01M15 9h.01M9 15c.83.67 1.83 1 3 1s2.17-.33 3-1"></path>
            </svg>
            <p className="text-base font-semibold text-text-primary mb-sp-1">
              No questions available
            </p>
            <p className="text-sm text-text-muted max-w-sm mx-auto mb-sp-4">{error}</p>
            <button
              onClick={() => onExit()}
              className="px-sp-5 py-sp-3 text-sm font-medium bg-accent text-white rounded-r-md hover:bg-accent-hover transition-colors"
            >
              Back to setup
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Graded: show summary
  if (examPhase === 'graded') {
    return (
      <SessionSummary
        questions={questions}
        answers={answers}
        totalTimeSeconds={examTotalSecs}
        onStartAnother={() => onExit()}
        onReviewIncorrect={() => onExit({ reviewIncorrect: true })}
      />
    );
  }

  const question = questions[currentIdx];
  const total = questions.length;
  const topicLabel = question.subject_area || '';
  const moduleBadge = (question.module || '').startsWith('FLK') ? question.module : null;

  // Exam review phase
  if (examPhase === 'review') {
    return (
      <>
        <div className="flex items-center justify-between mb-sp-3">
          <div className="flex items-center gap-sp-2">
            {moduleBadge && (
              <span className="text-xs font-medium px-sp-2 py-[2px] rounded-full bg-flk1 text-white">
                {moduleBadge}
              </span>
            )}
            <span className="text-xs font-medium px-sp-2 py-[2px] rounded-full bg-amber text-white">
              Exam
            </span>
            <span className="text-sm font-medium text-text-secondary">{topicLabel}</span>
          </div>
          <div className="flex items-center gap-sp-3">
            <span className="text-sm font-semibold text-text-primary">{fmtTimer(countdown)}</span>
          </div>
        </div>
        <ExamReview
          questions={questions}
          answers={answers}
          flagged={flagged}
          currentIdx={currentIdx}
          elapsedSecs={totalExamSecs - countdown}
          onJump={(i) => {
            setCurrentIdx(i);
            setExamPhase('answering');
          }}
          onSubmit={handleSubmitExam}
          submitting={submittingExam}
        />
      </>
    );
  }

  // Answering phase
  // Progress segments
  const segments = questions.map((_, i) => {
    if (isExam) {
      if (flagged[i]) return 'bg-amber';
      if (answers[i] && answers[i].selected_answer) return 'bg-accent';
      return 'bg-border-soft';
    }
    // Practice
    const a = answers[i];
    if (i === currentIdx && !submitted) return 'bg-accent';
    if (a) return a.correct ? 'bg-green' : 'bg-red';
    return 'bg-border-soft';
  });

  const isFlagged = !!flagged[currentIdx];
  const examSelected = selections[currentIdx] || (answers[currentIdx] && answers[currentIdx].selected_answer) || null;

  return (
    <>
      <div className="flex items-center justify-end mb-sp-3">
        <div className="flex items-center gap-sp-3">
          <span className="text-sm font-medium text-text-muted">
            {currentIdx + 1} / {total}
          </span>
          {isExam && (
            <>
              <span className="text-sm text-border-soft">·</span>
              <span
                className={`text-sm font-semibold ${
                  countdown < 60 ? 'text-red' : 'text-text-primary'
                }`}
              >
                {fmtTimer(countdown)}
              </span>
            </>
          )}
        </div>
      </div>

      <div className="flex gap-[3px] mb-sp-4">
        {segments.map((seg, i) => (
          <div key={i} className={`flex-1 h-[3px] rounded-[2px] ${seg}`} />
        ))}
      </div>

      {submitted && !isExam && answers[currentIdx] ? (
        <QuestionResult
          question={question}
          result={answers[currentIdx]}
          onNext={handleNextPractice}
          isLast={currentIdx + 1 >= total}
        />
      ) : (
        <>
          <QuestionCard
            question={question}
            selected={isExam ? examSelected : null}
            onSelect={isExam ? handleExamSelect : handlePracticeSubmit}
            mode={isExam ? 'exam' : 'practice'}
            disabled={!isExam && submitted}
          />

          {isExam && (
            <div className="flex items-center justify-between mt-sp-4">
              <button
                onClick={toggleFlag}
                className={`inline-flex items-center gap-sp-2 px-sp-3 py-sp-2 text-sm font-semibold rounded-r-md transition-colors ${
                  isFlagged
                    ? 'bg-[#FDE68A] text-[#78350F] border border-[#F59E0B]'
                    : 'bg-[#FEF3C7] text-[#92400E] border border-[#F59E0B] hover:bg-[#FDE68A]'
                }`}
              >
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill={isFlagged ? 'currentColor' : 'none'}
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                >
                  <path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z"></path>
                </svg>
                {isFlagged ? 'Flagged for review' : 'Flag for review'}
              </button>
              <div className="flex gap-sp-2">
                <button
                  onClick={handleExamPrev}
                  disabled={currentIdx === 0}
                  className="px-sp-3 py-sp-2 text-sm font-medium text-text-secondary border border-border-soft rounded-r-md hover:bg-bg-page transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  Previous
                </button>
                <button
                  onClick={handleExamNext}
                  className="px-sp-3 py-sp-2 text-sm font-medium bg-accent text-white rounded-r-md hover:bg-accent-hover transition-colors"
                >
                  {currentIdx + 1 >= total ? 'Review' : 'Next'}
                </button>
              </div>
            </div>
          )}
        </>
      )}

      {!isExam && !submitted && (
        <div className="mt-sp-4 flex justify-end">
          <button
            onClick={() => onExit()}
            className="text-sm text-text-muted hover:text-text-primary transition-colors"
          >
            End session
          </button>
        </div>
      )}
    </>
  );
}
